import React from 'react';
import { ChevronDown, Download, FileOutput, FileSpreadsheet, FileText, Upload } from 'lucide-react';
import { Z_INDEX } from '../../../shared/constants/zIndexLayers';

/**
 * Console import / export toolbar buttons (shared across list pages).
 * Default formats: import Excel + CSV; export PDF + Excel + CSV.
 * Pass showJsonImportExport to expose JSON backup/migration options.
 */
export default function QawaemImportExportToolbar({
  importOpen,
  exportOpen,
  onToggleImport,
  onToggleExport,
  importRef,
  exportRef,
  importLabel,
  exportLabel,
  generatingLabel,
  isGeneratingPDF,
  onImportCSV,
  onImportExcel,
  onImportJSON,
  onExportPDF,
  onExportExcel,
  onExportCSV,
  onExportJSON,
  compact = false,
  showJsonImportExport = false,
}) {
  const importItems = [
    { onClick: onImportExcel, icon: FileSpreadsheet, color: 'text-green-500', label: 'Excel' },
    { onClick: onImportCSV, icon: FileText, color: 'text-red-400', label: 'CSV' },
  ];
  if (showJsonImportExport) {
    importItems.push({ onClick: onImportJSON, icon: FileText, color: 'text-yellow-500', label: 'JSON' });
  }

  return (
    <>
      <div className="qawaem-import-export-toolbar relative shrink-0" style={{ zIndex: Z_INDEX.LIST_TOOLBAR }} ref={importRef}>
        <button
          type="button"
          onClick={onToggleImport}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold shrink-0 border border-[#a67c3d]/45 bg-gradient-to-b from-[#faf8f4] to-[#f9f3ea] text-[#704820] hover:border-[#8a6530]/55"
        >
          <Upload size={16} className="shrink-0" />
          {!compact ? <span className="hidden sm:inline">{importLabel}</span> : null}
          <ChevronDown size={14} className={`shrink-0 transition-transform ${importOpen ? 'rotate-180' : ''}`} />
        </button>
        {importOpen ? (
          <div
            className="qawaem-portal-dropdown absolute top-[calc(100%+8px)] end-0 min-w-[140px] border rounded-2xl shadow-2xl"
            style={{ zIndex: Z_INDEX.LIST_DROPDOWN }}
          >
            <div className="p-2">
              {importItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.onClick}
                  className="w-full px-4 py-2.5 text-start transition-colors flex items-center gap-3 rounded-xl hover:bg-[#6e9b6e]/10 text-sm font-medium text-forest-900"
                >
                  <item.icon size={16} className={item.color} />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="qawaem-import-export-toolbar relative shrink-0" style={{ zIndex: Z_INDEX.LIST_TOOLBAR }} ref={exportRef}>
        <button
          type="button"
          onClick={onToggleExport}
          className="toolbar-export-trigger inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold shrink-0"
        >
          <Download size={16} className="shrink-0 opacity-80" />
          {!compact ? <span className="hidden sm:inline">{exportLabel}</span> : null}
          <ChevronDown size={14} className={`shrink-0 transition-transform opacity-70 ${exportOpen ? 'rotate-180' : ''}`} />
        </button>
        {exportOpen ? (
          <div
            className="qawaem-portal-dropdown toolbar-export-panel absolute top-[calc(100%+8px)] end-0"
            style={{ zIndex: Z_INDEX.LIST_DROPDOWN, minWidth: '180px' }}
          >
            <div className="toolbar-export-panel-head">
              <span>تصدير ذكي</span>
            </div>
            <ul className="p-1.5 space-y-0.5">
              <li>
                <button
                  type="button"
                  data-export-format="PDF"
                  onClick={onExportPDF}
                  disabled={isGeneratingPDF}
                  className="toolbar-export-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start disabled:opacity-50"
                >
                  <span className="toolbar-export-item-icon text-rose-500">
                    <FileText className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium text-forest-900">{isGeneratingPDF ? generatingLabel : 'PDF'}</span>
                </button>
              </li>
              <li>
                <button type="button" data-export-format="Excel" onClick={onExportExcel} className="toolbar-export-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start">
                  <span className="toolbar-export-item-icon text-emerald-500"><FileSpreadsheet className="h-4 w-4" /></span>
                  <span className="text-sm font-medium text-forest-900">Excel</span>
                </button>
              </li>
              <li>
                <button type="button" data-export-format="CSV" onClick={onExportCSV} className="toolbar-export-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start">
                  <span className="toolbar-export-item-icon text-violet-500"><FileOutput className="h-4 w-4" /></span>
                  <span className="text-sm font-medium text-forest-900">CSV</span>
                </button>
              </li>
              {showJsonImportExport ? (
                <li>
                  <button type="button" onClick={onExportJSON} className="toolbar-export-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start">
                    <span className="toolbar-export-item-icon text-yellow-500"><FileText className="h-4 w-4" /></span>
                    <span className="text-sm font-medium text-forest-900">JSON</span>
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>
    </>
  );
}
